(function () {
  const config = window.freeGiftPromotionConfig;
  if (!config || !config.enabled) return;

  const giftVariantId = String(config.variantId || '');
  const promotionCode = config.promotionCode || 'silk_mask_88';
  const thresholdCents = Number(config.thresholdCents || 0);
  const cartTeaserMinimumCents = Number(config.cartTeaserMinimumCents || 0);
  const giftValueCents = Number(config.giftValueCents || 0);
  const expectedCurrency = String(config.currency || 'GBP').toUpperCase();
  const giftHandles = [config.giftProductHandle, config.retailGiftHandle].filter(Boolean);
  const propertyName = '_free_gift_promo';
  const mutationEventStart = 'snoooze:cart-mutation-start';
  const mutationEventEnd = 'snoooze:cart-mutation-end';
  const cartUpdatedEvent = 'snoooze:cart-updated';

  let queue = Promise.resolve();
  let scheduledReconcile = null;
  let externalMutations = 0;
  let suppressObserversUntil = 0;
  let checkoutBypass = false;

  function ajaxUrl(base) {
    return /\.js(?:\?|$)/.test(base) ? base : `${base}.js`;
  }

  async function requestJson(url, options) {
    const response = await fetch(url, options);
    let payload = null;

    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    if (!response.ok || (payload && payload.status && payload.status >= 400)) {
      const message = payload && (payload.description || payload.message);
      throw new Error(message || `Cart request failed (${response.status})`);
    }

    return payload;
  }

  function jsonRequest(body) {
    return {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    };
  }

  function getCart() {
    return requestJson(ajaxUrl(routes.cart_url), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
  }

  function isGiftVariant(item) {
    return String(item.variant_id) === giftVariantId;
  }

  function isExcludedItem(item) {
    const type = String(item.product_type || '').toLowerCase();
    return isGiftVariant(item) ||
      giftHandles.includes(item.handle) ||
      item.gift_card === true ||
      type === 'gift card' ||
      type === 'gift_card' ||
      type === 'ghost_product' ||
      type === 'giftbox_ghost_product';
  }

  function eligibleSubtotal(cart) {
    return cart.items.reduce((total, item) => {
      return isExcludedItem(item) ? total : total + Number(item.final_line_price || 0);
    }, 0);
  }

  function isCurrencyEligible(cart) {
    return String(cart.currency || '').toUpperCase() === expectedCurrency;
  }

  function hasPromotionProperty(item) {
    return item.properties && item.properties[propertyName] === promotionCode;
  }

  function isGiftItem(item) {
    return isGiftVariant(item) && hasPromotionProperty(item);
  }

  function giftItems(cart) {
    return cart.items.filter(hasPromotionProperty);
  }

  function setCheckoutBusy(isBusy) {
    document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach((button) => {
      if (isBusy) {
        if (button.dataset.freeGiftLock !== 'true') {
          button.dataset.freeGiftLock = 'true';
          button.dataset.freeGiftWasDisabled = button.disabled ? 'true' : 'false';
        }
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (button.dataset.freeGiftLock === 'true') {
        if (button.dataset.freeGiftWasDisabled !== 'true') {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
        }
        delete button.dataset.freeGiftLock;
        delete button.dataset.freeGiftWasDisabled;
      }
    });
  }

  function setPromotionState(state, message) {
    document.querySelectorAll('[data-free-gift-promotion]').forEach((element) => {
      element.dataset.state = state;
      element.classList.toggle('is-syncing', state === 'syncing');
      element.classList.toggle('has-error', state === 'error');

      const messageElement = element.querySelector('[data-free-gift-message]');
      if (messageElement && message) messageElement.textContent = message;
    });
  }

  function setCartPromotionVisibility(subtotal) {
    document.querySelectorAll('.free-gift-promotion--cart').forEach((element) => {
      element.hidden = subtotal < cartTeaserMinimumCents;
    });
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: expectedCurrency,
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(cents / 100);
  }

  function removeGiftLines(items) {
    if (!items.length) return Promise.resolve();

    const updates = {};
    items.forEach((item) => {
      updates[item.key] = 0;
    });
    return requestJson(ajaxUrl(routes.cart_update_url), jsonRequest({ updates }));
  }

  function normalizeGiftLines(items, canonicalItem) {
    const updates = {};
    items.forEach((item) => {
      updates[item.key] = item.key === canonicalItem.key ? 1 : 0;
    });

    const needsUpdate = items.some((item) => updates[item.key] !== item.quantity);
    if (!needsUpdate) return Promise.resolve();
    return requestJson(ajaxUrl(routes.cart_update_url), jsonRequest({ updates }));
  }

  function addGift() {
    return requestJson(ajaxUrl(routes.cart_add_url), jsonRequest({
      items: [{
        id: giftVariantId,
        quantity: 1,
        properties: { [propertyName]: promotionCode }
      }]
    }));
  }

  function sectionInnerHtml(html, selector) {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const selected = parsed.querySelector(selector);
    return selected ? selected.innerHTML : null;
  }

  async function refreshCartSections(cart) {
    // Shopify can briefly return stale cart-footer totals while automatic
    // discounts are recalculated after a gift line is added or removed. On the
    // full cart page, reload once after the mutation so line items, PACK and
    // subtotal are rendered from the same server-side cart snapshot. The next
    // reconcile is stable and does not reload again.
    if (document.querySelector('#MainContent #main-cart-items')) {
      setCheckoutBusy(true);
      window.location.reload();
      return;
    }

    const sectionNames = ['mini-cart', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];
    const separator = routes.cart_url.includes('?') ? '&' : '?';
    const url = `${routes.cart_url}${separator}sections=${sectionNames.join(',')}`;
    const sections = await requestJson(url, { credentials: 'same-origin' });

    suppressObserversUntil = Date.now() + 600;

    const miniCart = document.getElementById('mini-cart');
    if (miniCart && sections['mini-cart']) {
      const replacement = sectionInnerHtml(sections['mini-cart'], '.shopify-section');
      if (replacement !== null) miniCart.innerHTML = replacement;
    }

    const cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon && sections['cart-icon-bubble']) {
      const replacement = sectionInnerHtml(sections['cart-icon-bubble'], '.shopify-section');
      if (replacement !== null) cartIcon.innerHTML = replacement;
    }

    const mainCartItems = document.querySelector('#MainContent #main-cart-items');
    if (mainCartItems && sections['main-cart-items']) {
      const replacement = sectionInnerHtml(sections['main-cart-items'], '.js-contents');
      const contents = mainCartItems.querySelector('.js-contents');
      if (contents && replacement !== null) contents.innerHTML = replacement;
    }

    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter && sections['main-cart-footer']) {
      const replacement = sectionInnerHtml(sections['main-cart-footer'], '.js-contents');
      const contents = mainCartFooter.querySelector('.js-contents');
      if (contents && replacement !== null) contents.innerHTML = replacement;
    }

    const isEmpty = Number(cart && cart.item_count) === 0;
    const cartItems = document.querySelector('cart-items');
    if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);
    if (mainCartFooter) mainCartFooter.classList.toggle('is-empty', isEmpty);

    setCheckoutBusy(true);
  }

  function assertCartContract(cart) {
    const gifts = giftItems(cart);
    const qualifies = isCurrencyEligible(cart) && eligibleSubtotal(cart) >= thresholdCents;

    if (!qualifies && gifts.length) {
      throw new Error('Free gift remained in an ineligible cart.');
    }

    if (qualifies) {
      if (gifts.length !== 1 || gifts[0].quantity !== 1 || !isGiftItem(gifts[0])) {
        throw new Error('Free gift quantity or promotion marker is invalid.');
      }
      if (Number(gifts[0].final_line_price) !== 0 || Number(gifts[0].original_line_price) !== giftValueCents) {
        throw new Error('The gift discount or retail mask price is invalid.');
      }
    }

    return { qualifies, gifts, subtotal: eligibleSubtotal(cart) };
  }

  async function runReconcile(options) {
    const shouldRefresh = !options || options.refresh !== false;
    let changed = false;

    setCheckoutBusy(true);
    setPromotionState('syncing', 'Checking your free gift…');

    try {
      let cart = await getCart();
      let gifts = giftItems(cart);
      const qualifies = isCurrencyEligible(cart) && eligibleSubtotal(cart) >= thresholdCents;

      if (!qualifies) {
        if (gifts.length) {
          await removeGiftLines(gifts);
          changed = true;
        }
      } else {
        let canonicalGift = gifts.find(isGiftItem);
        if (!canonicalGift) {
          if (gifts.length) await removeGiftLines(gifts);
          await addGift();
          changed = true;
        } else if (gifts.length !== 1 || canonicalGift.quantity !== 1) {
          await normalizeGiftLines(gifts, canonicalGift);
          changed = true;
        }
      }

      if (changed) cart = await getCart();

      if (isCurrencyEligible(cart) && eligibleSubtotal(cart) >= thresholdCents) {
        const verifiedGift = giftItems(cart).find(isGiftItem);
        const giftIsFree = verifiedGift && Number(verifiedGift.final_line_price) === 0;
        const basePriceIsCorrect = verifiedGift && Number(verifiedGift.original_line_price) === giftValueCents;

        if (!giftIsFree || !basePriceIsCorrect) {
          await removeGiftLines(giftItems(cart));
          changed = true;
          cart = await getCart();
          if (shouldRefresh) await refreshCartSections(cart);
          throw new Error('The inactive or misconfigured gift discount did not make exactly one £22.70 gift free.');
        }
      }

      const contract = assertCartContract(cart);

      if (changed && shouldRefresh) await refreshCartSections(cart);

      setCartPromotionVisibility(contract.subtotal);

      if (contract.qualifies) {
        setPromotionState('added', `${formatMoney(giftValueCents)} silk mask added at £0.`);
      } else {
        const remaining = Math.max(0, thresholdCents - contract.subtotal);
        setPromotionState('locked', `Add ${formatMoney(remaining)} more.`);
      }

      document.dispatchEvent(new CustomEvent('snoooze:free-gift-reconciled', {
        detail: { changed, ...contract }
      }));

      return { cart, changed, ...contract };
    } catch (error) {
      console.error('[Free gift promotion]', error);
      setPromotionState('error', 'We could not secure your free gift. Please retry checkout or contact us.');
      throw error;
    } finally {
      setCheckoutBusy(false);
    }
  }

  function reconcile(options) {
    queue = queue.catch(() => undefined).then(() => runReconcile(options));
    return queue;
  }

  function scheduleReconcile() {
    if (Date.now() < suppressObserversUntil) return;
    window.clearTimeout(scheduledReconcile);
    scheduledReconcile = window.setTimeout(() => {
      reconcile().catch(() => undefined);
    }, 120);
  }

  function waitForCartIdle(timeoutMs) {
    const startedAt = Date.now();
    return new Promise((resolve, reject) => {
      const check = () => {
        if (externalMutations === 0) {
          resolve();
        } else if (Date.now() - startedAt >= timeoutMs) {
          reject(new Error('Cart update did not finish before checkout.'));
        } else {
          window.setTimeout(check, 50);
        }
      };
      check();
    });
  }

  document.addEventListener(mutationEventStart, () => {
    externalMutations += 1;
    setCheckoutBusy(true);
  });

  document.addEventListener(mutationEventEnd, () => {
    externalMutations = Math.max(0, externalMutations - 1);
    if (externalMutations === 0) scheduleReconcile();
  });

  document.addEventListener(cartUpdatedEvent, scheduleReconcile);

  document.addEventListener('submit', async (event) => {
    const submitter = event.submitter || document.activeElement;
    if (checkoutBypass || !submitter || submitter.name !== 'checkout') return;

    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      await waitForCartIdle(8000);
      await reconcile({ refresh: false });
      checkoutBypass = true;
      window.location.assign('/checkout');
    } catch (error) {
      checkoutBypass = false;
    }
  }, true);

  window.freeGiftPromotion = {
    config,
    getCart,
    eligibleSubtotal,
    assertCartContract,
    reconcile
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      reconcile().catch(() => undefined);
    }, { once: true });
  } else {
    reconcile().catch(() => undefined);
  }
})();
